import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faCalendarDays,
  faListCheck,
  faPhone
} from '@fortawesome/pro-solid-svg-icons'
import { type Action, ActionType } from '~/domains/actions/types'
import { getActionTypeIcon } from './actionTypeIcons'

const isTaskCall = (task: Action) => task.type === ActionType.CALL

const isTaskMeeting = (task: Action) =>
  task.type === ActionType.MEETING || task.type === ActionType.MEETING_CONTACT

/** A free task: the generic TASK type without a custom action type. */
export const isFreeTask = (task: Action) =>
  task.type === ActionType.TASK && !task.actionType

export const hasTaskOutcome = (task: Action) =>
  isTaskCall(task) || isTaskMeeting(task)

/** Gendered wording: a call or a meeting is masculine, a task is feminine. */
export const getTaskWording = (masculine: boolean) => ({
  scheduled: masculine ? 'Prévu' : 'Prévue',
  created: masculine ? 'Créé' : 'Créée',
  assigned: masculine ? 'Assigné' : 'Assignée',
  unassigned: masculine ? 'Non assigné' : 'Non assignée',
  treated: masculine ? 'Terminé' : 'Terminée',
  cancelled: masculine ? 'Annulé' : 'Annulée'
})

export const getTaskWordingFor = (task: Action) =>
  getTaskWording(hasTaskOutcome(task))

/** Task-oriented label: the backend `typeName` carries the legacy history wording ("Planifier un RDV"). */
export const getTaskLabel = (task: Action): string => {
  if (task.actionType?.name) {
    return task.actionType.name
  }
  if (isTaskCall(task)) {
    return 'Appel'
  }
  if (isTaskMeeting(task)) {
    return 'Rendez-vous'
  }
  if (task.type === ActionType.TASK) {
    return 'Tâche'
  }
  return task.typeName
}

export const getTaskTitle = (task: Action): string =>
  task.subject?.trim() ? task.subject : getTaskLabel(task)

export const getTaskIcon = (task: Action): IconDefinition => {
  const customIcon = getActionTypeIcon(task.actionType?.icon)
  if (customIcon) {
    return customIcon
  }
  if (isTaskCall(task)) {
    return faPhone
  }
  if (isTaskMeeting(task)) {
    return faCalendarDays
  }
  return faListCheck
}

export const getTaskColor = (task: Action): string | null =>
  task.actionType?.color ?? null

type TaskOutcome = {
  label: string
  variant: 'success' | 'warning' | 'neutral'
}

/** Outcome of a treated task, worded per type: a call or a meeting (masculine) is reached or not, a task (feminine) is done. */
export const getTaskOutcome = (task: Action): TaskOutcome | null => {
  const withOutcome = hasTaskOutcome(task)
  if (task.isCancelled) {
    return { label: getTaskWording(withOutcome).cancelled, variant: 'neutral' }
  }
  if (task.isCompleted === true) {
    return { label: withOutcome ? 'Abouti' : 'Faite', variant: 'success' }
  }
  if (task.isCompleted === false) {
    return { label: 'Non abouti', variant: 'warning' }
  }
  return null
}
